import { useNavigate } from "react-router-dom";

/**
 * CategoryPill — neutral category indicator.
 * - If the category has a user-uploaded `image`, show it as a tiny thumbnail.
 * - Otherwise show a neutral square placeholder (no icon, no emoji).
 *
 * Props:
 *   category: { id, category_name, image }  (or null/undefined)
 *   size: "sm" | "md" (default "sm")
 *   muted: boolean — render text in muted colour
 */
export default function CategoryPill({ category, size = "sm", muted = false, testid }) {
  const navigate = useNavigate();
  const dim = size === "md" ? "h-6 w-6" : "h-5 w-5";
  const text = size === "md" ? "text-sm" : "text-xs";
  const padding = size === "md" ? "px-2.5 py-1" : "px-2 py-1";
  const isUncat = !category;
  const label = category?.category_name || "Uncategorised";
  const uncatClasses = isUncat
    ? "italic border-dashed text-[hsl(var(--loss))] dark:text-[hsl(var(--gold))]"
    : "";
  const go = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isUncat) {
      navigate("/items?category_id=__UNCAT__");
    } else {
      navigate(`/items?category_id=${category.id}`);
    }
  };
  return (
    <span
      data-testid={testid || (isUncat ? "category-pill-uncat" : "category-pill")}
      title={isUncat ? "Click to view all uncategorised items" : `Click to view all items in ${label}`}
      role="link"
      tabIndex={0}
      onClick={go}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") go(e); }}
      className={`inline-flex items-center gap-1.5 ${padding} rounded-md bg-[hsl(var(--muted))] border border-[hsl(var(--border))] ${text} ${muted ? "vp-muted" : ""} ${uncatClasses} max-w-full cursor-pointer hover:bg-[hsl(var(--bg))] transition-colors duration-fast`}
    >
      {category?.image ? (
        <img
          src={category.image}
          alt=""
          className={`${dim} object-cover rounded-sm border border-[hsl(var(--border))] shrink-0`}
        />
      ) : (
        <span
          aria-hidden="true"
          className={`${dim} rounded-sm bg-[hsl(var(--bg))] border border-[hsl(var(--border))] shrink-0`}
        />
      )}
      <span className="truncate">{label}</span>
    </span>
  );
}

/**
 * CategoryThumb — just the thumbnail or placeholder (no label, no chip).
 * Use this in Category cards or larger detail views.
 */
export function CategoryThumb({ image, size = "lg", testid }) {
  const dim = size === "lg" ? "w-12 h-12" : size === "md" ? "w-10 h-10" : "w-8 h-8";
  return image ? (
    <img
      data-testid={testid || "category-thumb"}
      src={image}
      alt=""
      className={`${dim} object-cover rounded-xl border border-[hsl(var(--border))]`}
    />
  ) : (
    <span
      data-testid={testid || "category-thumb"}
      aria-hidden="true"
      className={`${dim} rounded-xl bg-[hsl(var(--muted))] border border-[hsl(var(--border))] inline-block`}
    />
  );
}
